import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCompanies } from "../../hooks/useCompanies";
import { createCompaniesColumns } from "../CompanyColumns/CompanyColumns";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import CompanyListFilters from "../CompanyListFilters/CompanyListFilters";
import { Button } from "../../../../../../components/Button/Button";
import { Company, addSyncingCompany } from "../../slices/companiesSlice";
import DataTable from "@/components/Datatable/Datatable";
import { SyncLogsModal } from "../SyncLogsModal/SyncLogsModal";
import { useToast } from "@/components/ui/use-toast";
import { triggerCompanySync } from "../../api/companySyncApi";
import { CompanySyncSubscriptions } from "../../hooks/useCompanySyncSubscriptions";
import { RootState } from "@/app/rootReducer";

const CompanyList: React.FC = () => {
  const {
    companies,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleFilterChange,
    handleSortingChange,
    filters,
  } = useCompanies();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const syncingCompanyIds = useSelector(
    (state: RootState) => state.companies.syncingCompanyIds,
  );

  const [selectedCompanyForSyncLogs, setSelectedCompanyForSyncLogs] =
    useState<Company | null>(null);
  const [isSyncLogsModalOpen, setIsSyncLogsModalOpen] = useState(false);

  const handleEdit = useCallback(
    (uuid: string) => {
      navigate(`/admin/companies/${uuid}/edit`);
    },
    [navigate],
  );

  const handleViewSyncLogs = useCallback((company: Company) => {
    setSelectedCompanyForSyncLogs(company);
    setIsSyncLogsModalOpen(true);
  }, []);

  const handleSyncCompany = useCallback(
    async (company: Company) => {
      if (!company.id) {
        toast({
          title: "Error",
          description: "Company ID not found.",
          variant: "destructive",
        });
        return;
      }

      try {
        await triggerCompanySync(company.id);
        // Start tracking this company for real-time sync updates
        dispatch(addSyncingCompany(company.id));
        toast({
          title: "Sync Initiated",
          description: `Sync started for ${company.companyName}`,
        });
      } catch (error) {
        toast({
          title: "Sync Failed",
          description: "Failed to initiate sync. Please try again.",
          variant: "destructive",
        });
      }
    },
    [toast, dispatch],
  );

  const columns = useMemo(
    () =>
      createCompaniesColumns({
        handleEdit,
        handleViewSyncLogs,
        handleSyncCompany,
        syncingCompanyIds,
      }),
    [handleEdit, handleViewSyncLogs, handleSyncCompany, syncingCompanyIds],
  );

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => navigate("/admin/companies/create")}>
          Create Company
        </Button>
      }
      error={error}
      title="Companies"
    >
      <CompanyListFilters
        filters={filters}
        onFilterChange={(filterKey: string, value: string | undefined) => {
          handleFilterChange(filterKey, value);
        }}
      />

      <div className="relative">
        <DataTable<Company>
          columns={columns}
          data={companies}
          error={error}
          loading={loading}
          noDataMessage="No companies found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.id}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>

      <SyncLogsModal
        company={selectedCompanyForSyncLogs}
        companyId={selectedCompanyForSyncLogs?.id ?? null}
        isOpen={isSyncLogsModalOpen}
        onClose={() => setIsSyncLogsModalOpen(false)}
      />

      {/* Subscription handlers for real-time sync status updates */}
      <CompanySyncSubscriptions />
    </MainPageWrapper>
  );
};

export default CompanyList;
